/**
 * Arche Asset Bytes — client-side entity executor.
 *
 * Runs in the HOST page (platform origin), never in the plugin iframe. A
 * plugin's sandbox has `connect-src 'none'`, so it can show a library asset
 * through `<img src>` / `<audio src>` but cannot read its bytes — and Web
 * Audio (`decodeAudioData`), canvas pixel work and the like need the bytes.
 * The host fetches them on the plugin's behalf:
 *
 *  1. The plugin first calls the `arche_assets_get_url` bridge op as it always
 *     has. The SERVER enforces `assets:read` from the bridge token, picks the
 *     published / test / draft manifest, and returns a root-relative URL whose
 *     HMAC binds (archeId, assetId, manifestId, userId, exp).
 *  2. The plugin hands those URLs to this entity
 *     (`{ verb: 'read', signedUrls: [...] }`). The executor accepts a URL only
 *     if its path names the CALLING arche (taken from the host's own context,
 *     never the input) and its `u=` names the calling user. A plugin therefore
 *     reads exactly the bytes it was already allowed to load — holding the URL
 *     is the proof the server granted it.
 *  3. The host fetches each URL from the API origin with no credentials (the
 *     signature is the only authorization the byte route takes) and returns
 *     `{ results }`, one `{ contentType, bytes, sizeBytes }` or `{ error }` per
 *     URL, in input order.
 *
 * One call reads many URLs, the way a content pack's `read` takes many paths.
 * A game loads its sounds together at startup, and the bridge blocks any one
 * client entity called more than 50 times in 5 seconds (its runaway-loop
 * guard), so one call per asset would leave every sound after the fiftieth
 * silent. The SDK sends one call per batch (`useArcheAssetBytes().getMany`,
 * and `getBytes` calls made together are coalesced into one).
 *
 * Billing is unchanged by this path: the byte route records the egress
 * against the library's payer on every fetch, exactly as an `<img>` load does.
 *
 * Expected failures are RESULTS, not throws: the bridge forwards only an
 * error's message, so `{ error: { code, message } }` is how typed codes reach
 * the SDK (`useArcheAssetBytes()`). A URL's own failure is its result's
 * `{ error }`; only a malformed call fails as a whole.
 *
 * Host services arrive through `configureArcheAssetBytesHost` at registration
 * (`client/src/lib/register-client-entities.ts`), because the executor context
 * carries only `{ userId, archeId }`.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const ARCHE_ASSET_BYTES_ENTITY_ID = 'ent_intg_arche_asset_bytes_00000';

/**
 * Most bytes one call returns, across all its URLs — the library's default
 * per-upload cap, so any single asset can still be read on its own. Bounds the
 * host's memory per call: a batch that would pass it returns
 * `ASSET_TOO_LARGE` for the assets that did not fit (the SDK re-reads those
 * one at a time).
 */
export const ARCHE_ASSET_BYTES_MAX_BYTES = 50 * 1024 * 1024;

/** Most URLs one call may name — a content pack `read`'s path limit. */
export const ARCHE_ASSET_BYTES_MAX_URLS = 100;

/** Fetches in flight at once within one call (a browser's per-host connection pool). */
const FETCH_CONCURRENCY = 6;

/** The synthetic tenant prefix the Fias Forge test channel runs under. */
const TEST_TENANT_PREFIX = 'test_';

/** Path shape of a signed asset URL: `/v1/arches/<archeId>/assets/<assetId>`. */
const SIGNED_ASSET_PATH_RE = /^\/v1\/arches\/([^/]+)\/assets\/([^/]+)$/;

export interface ArcheAssetBytesHost {
  /**
   * Origin the platform API answers from (`https://api.<env>.fias.io`, or the
   * page's own origin where the API is same-origin). Signed asset URLs are
   * root-relative and resolve against it.
   */
  apiOrigin: string;
  /** Injected for tests; defaults to the global `fetch`. */
  fetch?: typeof fetch;
}

export interface ArcheAssetBytesInput {
  verb: 'read';
  /**
   * 1–{@link ARCHE_ASSET_BYTES_MAX_URLS} `signedUrl`s exactly as
   * `arche_assets_get_url` / `arche_assets_list` returned them.
   */
  signedUrls: string[];
}

export type ArcheAssetBytesErrorCode =
  | 'INVALID_INPUT'
  | 'ASSETS_UNAVAILABLE_IN_PREVIEW'
  | 'ASSET_URL_FORBIDDEN'
  | 'ASSET_URL_EXPIRED'
  | 'ASSET_TOO_LARGE'
  | 'ASSET_UNAVAILABLE';

export interface ArcheAssetBytesError {
  code: ArcheAssetBytesErrorCode;
  message: string;
}

/** One URL's outcome. */
export type ArcheAssetBytesFileResult =
  | { contentType: string; bytes: ArrayBuffer; sizeBytes: number }
  | { error: ArcheAssetBytesError };

/** `results[i]` is the outcome of `signedUrls[i]`; `{ error }` alone means the call was refused. */
export type ArcheAssetBytesResult =
  | { results: ArcheAssetBytesFileResult[] }
  | { error: ArcheAssetBytesError };

let host: ArcheAssetBytesHost | null = null;

/** Called once by the host at registration. Replaces any previous host. */
export function configureArcheAssetBytesHost(next: ArcheAssetBytesHost): void {
  host = next;
}

/** For tests: back to "no host configured". */
export function resetArcheAssetBytesHostForTests(): void {
  host = null;
}

function error(code: ArcheAssetBytesErrorCode, message: string): { error: ArcheAssetBytesError } {
  return { error: { code, message } };
}

function tooLarge(alone: boolean): { error: ArcheAssetBytesError } {
  return alone
    ? error('ASSET_TOO_LARGE', `the asset is larger than ${ARCHE_ASSET_BYTES_MAX_BYTES} bytes`)
    : error(
        'ASSET_TOO_LARGE',
        `reading this asset would pass the ${ARCHE_ASSET_BYTES_MAX_BYTES}-byte per-call limit; read it on its own`,
      );
}

/** Preview surfaces have no signed asset URLs (the host stubs the asset ops there). */
function isPreviewArche(archeId: string): boolean {
  return archeId.startsWith('preview_') || archeId === 'dev_preview' || archeId.startsWith('dev_');
}

/**
 * The arche a signed URL must name. The Forge test channel's bridge runs as
 * `test_<archeId>`, but the server signs test-channel URLs for the real arche.
 */
function signingArcheId(contextArcheId: string): string {
  return contextArcheId.startsWith(TEST_TENANT_PREFIX)
    ? contextArcheId.slice(TEST_TENANT_PREFIX.length)
    : contextArcheId;
}

/**
 * The URL to fetch, or an error. Accepts only a root-relative signed asset
 * path for the calling arche and user — never an absolute URL, so the host
 * cannot be pointed at another origin.
 */
function resolveSignedUrl(
  signedUrl: unknown,
  context: ClientEntityInvocationContext,
  apiOrigin: string,
): { url: string } | { error: ArcheAssetBytesError } {
  if (
    typeof signedUrl !== 'string' ||
    signedUrl.length === 0 ||
    signedUrl.length > 2048 ||
    !signedUrl.startsWith('/') ||
    signedUrl.startsWith('//') ||
    signedUrl.includes('\\')
  ) {
    return error('INVALID_INPUT', 'signedUrl must be a URL returned by the asset library');
  }
  let parsed: URL;
  try {
    parsed = new URL(signedUrl, 'https://asset-url.invalid');
  } catch {
    return error('INVALID_INPUT', 'signedUrl must be a URL returned by the asset library');
  }
  const match = SIGNED_ASSET_PATH_RE.exec(parsed.pathname);
  if (!match) {
    return error('INVALID_INPUT', 'signedUrl must be a URL returned by the asset library');
  }
  let urlArcheId: string;
  try {
    urlArcheId = decodeURIComponent(match[1]);
  } catch {
    return error('INVALID_INPUT', 'signedUrl must be a URL returned by the asset library');
  }
  if (urlArcheId !== signingArcheId(context.archeId)) {
    return error('ASSET_URL_FORBIDDEN', "signedUrl does not belong to this arche's asset library");
  }
  if (parsed.searchParams.get('u') !== context.userId) {
    return error('ASSET_URL_FORBIDDEN', 'signedUrl was issued for a different user');
  }
  return { url: `${apiOrigin.replace(/\/$/, '')}${parsed.pathname}${parsed.search}` };
}

/** Runs `task` over `items`, at most `limit` at a time. */
async function mapLimited<T>(items: T[], limit: number, task: (item: T) => Promise<void>) {
  const queue = [...items];
  await Promise.all(
    Array.from({ length: Math.min(limit, queue.length) }, async () => {
      for (let next = queue.shift(); next !== undefined; next = queue.shift()) await task(next);
    }),
  );
}

/** Byte budget shared by the reads of one call. */
interface ReadBudget {
  remaining: number;
}

async function readSignedUrl(
  url: string,
  fetchImpl: typeof fetch,
  budget: ReadBudget,
  alone: boolean,
): Promise<ArcheAssetBytesFileResult> {
  let response: Response;
  try {
    response = await fetchImpl(url, { credentials: 'omit', mode: 'cors' });
  } catch {
    return error('ASSET_UNAVAILABLE', 'the asset could not be reached');
  }
  if (response.status === 410) {
    // An expired URL and a suspended asset both answer 410; a fresh
    // `get_url` resolves the first and reports the second.
    return error('ASSET_URL_EXPIRED', 'the asset URL has expired; request a fresh one');
  }
  if (!response.ok) {
    return error('ASSET_UNAVAILABLE', `the asset could not be read (${response.status})`);
  }
  // Reserve the declared length before reading, so reads in flight together
  // cannot pass the budget between them; true it up once the bytes are in (a
  // length header can be absent or wrong).
  const lengthHeader = response.headers.get('content-length');
  const declared = lengthHeader === null ? 0 : Number(lengthHeader);
  if (!Number.isFinite(declared) || declared > budget.remaining) {
    void response.body?.cancel().catch(() => undefined);
    return tooLarge(alone);
  }
  budget.remaining -= declared;
  let bytes: ArrayBuffer;
  try {
    bytes = await response.arrayBuffer();
  } catch {
    budget.remaining += declared;
    return error('ASSET_UNAVAILABLE', 'the asset could not be read');
  }
  budget.remaining -= bytes.byteLength - declared;
  if (budget.remaining < 0) {
    budget.remaining += bytes.byteLength;
    return tooLarge(alone);
  }
  return {
    contentType: response.headers.get('content-type') ?? 'application/octet-stream',
    bytes,
    sizeBytes: bytes.byteLength,
  };
}

export async function archeAssetBytesExecutor(
  input: ArcheAssetBytesInput,
  context: ClientEntityInvocationContext,
): Promise<ArcheAssetBytesResult> {
  const value = input as { verb?: unknown; signedUrls?: unknown } | null;
  if (value?.verb !== 'read') return error('INVALID_INPUT', 'verb must be "read"');
  const signedUrls = value.signedUrls;
  if (
    !Array.isArray(signedUrls) ||
    signedUrls.length === 0 ||
    signedUrls.length > ARCHE_ASSET_BYTES_MAX_URLS
  ) {
    return error(
      'INVALID_INPUT',
      `signedUrls must list 1–${ARCHE_ASSET_BYTES_MAX_URLS} URLs returned by the asset library`,
    );
  }
  if (isPreviewArche(context.archeId)) {
    return error(
      'ASSETS_UNAVAILABLE_IN_PREVIEW',
      'asset bytes are only available to published plugins, not in preview',
    );
  }
  const h = host;
  if (!h) return error('ASSET_UNAVAILABLE', 'asset bytes are not available on this host');

  const fetchImpl = h.fetch ?? fetch;
  const budget: ReadBudget = { remaining: ARCHE_ASSET_BYTES_MAX_BYTES };
  const alone = signedUrls.length === 1;
  const results: ArcheAssetBytesFileResult[] = new Array(signedUrls.length);
  const toFetch: Array<{ index: number; url: string }> = [];
  signedUrls.forEach((signedUrl: unknown, index) => {
    const resolved = resolveSignedUrl(signedUrl, context, h.apiOrigin);
    if ('error' in resolved) results[index] = resolved;
    else toFetch.push({ index, url: resolved.url });
  });
  await mapLimited(toFetch, FETCH_CONCURRENCY, async ({ index, url }) => {
    results[index] = await readSignedUrl(url, fetchImpl, budget, alone);
  });
  return { results };
}

export const archeAssetBytesDefinition = {
  entityId: ARCHE_ASSET_BYTES_ENTITY_ID,
  rateLimitPerMinute: 120,
  pluginPermission: 'entities:client_invoke',
  executor: archeAssetBytesExecutor,
} as const satisfies ClientEntityDefinition<ArcheAssetBytesInput, ArcheAssetBytesResult>;
